/**
 * The diary's "Activity & body" block: the day's watch numbers (sent by the phone app from Health
 * Connect, or typed in), which app each number came from, and how the day's burn was worked out.
 */
import { useState, type ReactElement } from 'react';
import type { DayBurn } from '../lib/energy';
import { formatDateTime, formatTime } from '../lib/dates';
import { DEFAULT_ACTIVITY_FIELDS } from '../lib/diaryLayout';
import type { ActivityDay, ActivityField, ActivityOrigin, UnitSystem } from '../lib/types';
import { Icon } from './Icon';
import { ManualActivityModal } from './ManualActivityModal';
import { BurnBreakdown } from './Panels';

const KNOWN_APPS: Readonly<Record<string, string>> = {
  'com.sec.android.app.shealth': 'Samsung Health',
  'com.fitbit.FitbitMobile': 'Fitbit',
  'com.google.android.apps.fitness': 'Google Fit',
  'com.google.android.apps.healthdata': 'Health Connect',
  'com.wittdevelopment.mightypeanut.lifting': 'Mighty Peanut Lifting',
  'com.wittdevelopment.mightypeanut.diet': 'Mighty Peanut Diet',
  'com.garmin.android.apps.connectmobile': 'Garmin Connect',
  'com.ouraring.oura': 'Oura',
  'com.whoop.android': 'WHOOP',
};

export const originName = (o: ActivityOrigin): string => o.name ?? KNOWN_APPS[o.package] ?? o.package;

const fmt = (n: number): string => Math.round(n).toLocaleString();
const hm = (min: number): string => `${Math.floor(min / 60)} h ${Math.round(min % 60)} min`;

function metric(field: ActivityField, row: ActivityDay, units: UnitSystem, partial: boolean): { label: string; value: string } | null {
  switch (field) {
    case 'active_kcal': return row.active_kcal === null ? null : { label: partial ? 'Active calories so far' : 'Active calories', value: `${fmt(row.active_kcal)} kcal` };
    case 'total_kcal': return row.total_kcal === null ? null : { label: 'Watch total calories', value: `${fmt(row.total_kcal)} kcal` };
    case 'steps': return row.steps === null ? null : { label: 'Steps', value: fmt(row.steps) };
    case 'distance': return row.distance_m === null ? null : { label: 'Distance', value: units === 'imperial' ? `${(row.distance_m / 1609.344).toFixed(1)} mi` : `${(row.distance_m / 1000).toFixed(1)} km` };
    case 'heart_rate': return row.avg_hr === null ? null : { label: 'Heart rate', value: `${fmt(row.avg_hr)} avg${row.min_hr !== null ? ` · ${fmt(row.min_hr)} low` : ''}${row.max_hr !== null ? ` · ${fmt(row.max_hr)} high` : ''} bpm` };
    case 'resting_hr': return row.resting_hr === null ? null : { label: 'Resting heart rate', value: `${fmt(row.resting_hr)} bpm` };
    case 'hrv': return row.hrv_rmssd_ms === null ? null : { label: 'HRV (RMSSD)', value: `${fmt(row.hrv_rmssd_ms)} ms` };
    case 'sleep': return row.sleep_minutes === null ? null : { label: 'Sleep', value: hm(row.sleep_minutes) };
    case 'spo2': return row.spo2_pct === null ? null : { label: 'Blood oxygen', value: `${row.spo2_pct.toFixed(1)}%` };
    case 'respiratory_rate': return row.respiratory_rate === null ? null : { label: 'Breathing rate', value: `${row.respiratory_rate.toFixed(1)} /min` };
    case 'vo2max': return row.vo2max === null ? null : { label: 'VO2 max', value: row.vo2max.toFixed(1) };
    case 'exercises': return null;
  }
}

export function ActivityPanel({ day, rows, fields, burn, units }: {
  day: string;
  /** Every row for the day (both sources). */
  rows: ReadonlyArray<ActivityDay>;
  fields: ReadonlyArray<ActivityField> | undefined;
  burn: DayBurn;
  units: UnitSystem;
}): ReactElement {
  const [editing, setEditing] = useState<boolean>(false);
  const manual: ActivityDay | undefined = rows.find((r) => r.source === 'manual');
  const phone: ActivityDay | undefined = rows.find((r) => r.source === 'health_connect');
  const used: ActivityDay | undefined = manual ?? phone;
  const show: ReadonlyArray<ActivityField> = fields && fields.length > 0 ? fields : DEFAULT_ACTIVITY_FIELDS;
  const partial: boolean = burn.partial;
  const tiles = used ? show.map((f) => metric(f, used, units, partial)).filter((t): t is { label: string; value: string } => t !== null) : [];
  const withNumbers: ActivityOrigin[] = (phone?.origins ?? []).filter((o) => o.active_kcal !== undefined || o.total_kcal !== undefined || o.steps !== undefined
    || o.exercise_minutes !== undefined || o.avg_hr !== undefined || o.sleep_minutes !== undefined);
  const uncovered: number = phone?.steps_uncovered ?? 0;

  return (
    <div className="activity-panel">
      <div className="ap-source">
        {used === undefined ? (
          <span className="muted">No watch data for this day. The Mighty Peanut Diet phone app sends it from Health Connect; you can also type the numbers in.</span>
        ) : used.source === 'manual' ? (
          <span className="muted"><Icon name="edit" size={14} /> Typed in by you{phone ? ` (used instead of the phone’s ${phone.active_kcal !== null ? `${fmt(phone.active_kcal)} active kcal` : 'numbers'})` : ''}.</span>
        ) : (
          <span className="muted"><Icon name="watch" size={14} /> From your phone{phone?.origins.length ? ` (${phone.origins.filter((o) => o.counted !== false).map(originName).join(', ')})` : ''}{phone?.synced_at ? `, updated ${formatDateTime(phone.synced_at)}` : ''}.</span>
        )}
        <button type="button" className="btn btn-sm btn-ghost" onClick={() => setEditing(true)}><Icon name="edit" size={15} /> {manual ? 'Edit' : 'Enter numbers'}</button>
      </div>

      {tiles.length > 0 && (
        <div className="ap-tiles">
          {tiles.map((t) => <div key={t.label} className="ap-tile"><span>{t.label}</span><b className="num">{t.value}</b></div>)}
        </div>
      )}

      {used && show.includes('exercises') && used.exercises.length > 0 && (
        <ul className="ap-exercises">
          {used.exercises.map((x, i) => (
            <li key={i}><b>{x.title ?? x.type.replace(/_/g, ' ')}</b> <span className="faint">{Math.round(x.minutes)} min{x.start ? ` · ${formatTime(x.start)}` : ''}</span></li>
          ))}
        </ul>
      )}

      {burn.kcal !== null && <div className="ap-burn"><span className="faint">Burn</span> <BurnBreakdown burn={burn} /></div>}

      {withNumbers.length > 0 && (
        <details className="ap-origins">
          <summary>Where these numbers come from ({withNumbers.length === 1 ? '1 app' : `${withNumbers.length} apps`})</summary>
          <table className="ntable">
            <tbody>
              {withNumbers.map((o) => (
                <tr key={o.package}>
                  <td>{originName(o)} {o.counted === false && <span className="faint">(not counted)</span>}</td>
                  <td className="v num">{[
                    o.active_kcal !== undefined ? `${fmt(o.active_kcal)} active kcal` : null,
                    o.total_kcal !== undefined ? `${fmt(o.total_kcal)} total kcal` : null,
                    o.steps !== undefined ? `${fmt(o.steps)} steps` : null,
                    o.exercise_minutes ? `${fmt(o.exercise_minutes)} min exercise` : null,
                    o.avg_hr !== undefined ? `avg ${fmt(o.avg_hr)} bpm` : null,
                    o.sleep_minutes ? `${hm(o.sleep_minutes)} sleep` : null,
                  ].filter((x): x is string => x !== null).join(' · ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {uncovered > 0 && phone?.steps !== null && phone?.steps !== undefined && (
            <p className="cite" style={{ margin: '6px 0 0' }}>
              {fmt(uncovered)} of the {fmt(phone.steps)} steps were taken while no app was recording active calories, so the burn adds them as walking: about 0.5 kcal per kg of body weight per km, with your step length taken from your height.
            </p>
          )}
          <p className="cite" style={{ margin: '6px 0 0' }}>
            Health Connect counts overlapping data once, using the app priority order in its settings. When two apps record the same walk, only the one higher on that list counts, so the total can be less than the apps added together.
          </p>
        </details>
      )}
      {editing && <ManualActivityModal day={day} existing={manual ?? null} onClose={() => setEditing(false)} />}
    </div>
  );
}
